/**
 * 通道账户。
 *
 * **通道是平台专属资产。** 代理只看得到自己有权使用的那些通道的非敏感元信息
 *（币种、卡组织、限额），用来决定给自己的商户下发哪一个；成本参数完全不下发 ——
 * 代理知道平台的进价就没法谈价了。这一层过滤在后端（`mapChannel(row, includeCost)`），
 * 前端只是不去渲染不存在的字段。
 *
 * 两个「改了就出资损」的字段在界面上都带了显式警告：
 * - `settlement_model`：退款记账按退款时刻的模型分叉，有存量交易时后端会直接拒绝
 * - `cost_rate_bps`：上调成本会让低于新成本的下游费率规则全线创建失败
 */

import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { ApiError, request } from '../lib/api.ts';
import { useAsync, useMutation } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import { ScopeBanner } from '../components/scope-banner.tsx';
import type { Collection } from '../lib/api.ts';
import {
  Badge, Button, Card, CopyId, ErrorBox, Field, MinorAmount, ModeBadge, Modal, Select,
  StatusBadge, Tabs, TextInput, DetailGrid,
} from '../components/ui.tsx';
import { DataTable, type Column } from '../components/table.tsx';
import { formatDateTime } from '../lib/format.ts';
import { formatBps } from '../lib/money.ts';
import type { Channel, ChannelGroup, ChannelPaymentLink } from '../lib/types.ts';

interface CredentialRequirement {
  readonly channel_code: string;
  readonly required_keys: readonly string[];
}

/**
 * 各通道必需的凭证键名，**由后端下发**。
 *
 * 这张表以前在这里硬编码，注释写着「与后端 REQUIRED_CREDENTIAL_KEYS 逐字一致」，
 * 然后 2026-09-20 就漂了：后端把 publishableKey 加进 Stripe 的必填项，这里没跟，
 * 于是建 Stripe 通道的表单不渲染那个输入框 —— 点保存必然收到
 * 「缺少凭证字段: publishableKey」，而界面上根本没有地方能填它。
 * 一个自己解不开的死循环，只能改代码才出得来。
 *
 * 两份手抄的清单迟早会漂，注释拦不住。所以只留后端那一份。
 */
function useCredentialRequirements(): {
  map: Readonly<Record<string, readonly string[]>>;
  codes: readonly string[];
  loading: boolean;
  error: unknown;
} {
  const req = useAsync<{ data: readonly CredentialRequirement[] }>('/v1/channels/credential-requirements');
  const rows = req.data?.data ?? [];
  const map: Record<string, readonly string[]> = {};
  for (const r of rows) map[r.channel_code] = r.required_keys;
  return { map, codes: rows.map((r) => r.channel_code), loading: req.loading, error: req.error };
}

/**
 * 一条通道的付款页链接。
 *
 * 正常情况下每条通道恰好挂一条固定链接，就显示一个图标；挂了多条时每条一个图标，
 * 悬停看标题。新标签页打开，并且 noopener —— 付款页是对外的公开页面，
 * 不该拿到后台这个标签页的 window.opener。
 */
function PayLinks({ links }: { links: readonly ChannelPaymentLink[] }) {
  if (links.length === 0) return <span className="cell-muted">—</span>;
  return (
    <div className="row tight">
      {links.map((l) => (
        <a
          key={l.pay_url}
          href={l.pay_url}
          target="_blank"
          rel="noopener noreferrer"
          title={`${l.title}\n${l.pay_url}`}
          aria-label={`打开付款页：${l.title}`}
          style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--accent)' }}
          onClick={(e) => { e.stopPropagation(); }}
        >
          <ExternalLink size={16} />
        </a>
      ))}
    </div>
  );
}

export function ChannelsPage() {
  const session = useSession();
  const [mode, setMode] = useState('');
  const [status, setStatus] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Channel | null>(null);

  const list = useAsync<Collection<Channel>>(
    '/v1/channels',
    {
      query: {
        mode: mode === '' ? null : mode,
        status: status === '' ? null : status,
        channel_group_id: groupFilter === '' ? null : groupFilter,
      },
    },
    [mode, status, groupFilter],
  );

  // 空 deps：分组清单与筛选状态无关，跟着变会打成取数→重渲染→再取数的循环。
  const groups = useAsync<{ data: readonly ChannelGroup[] }>('/v1/channel-groups', {}, []);

  const canManage = session.isPlatform && session.can('admin');
  // 后端只对能看收款链接页面的人下发这个字段；任何一行带了它，就说明有权限
  const showLinks = (list.data?.data ?? []).some((row) => row.payment_links !== undefined);

  const columns: readonly Column<Channel>[] = [
    { key: 'channel', header: '通道', render: (row) => (
      <div className="stack tight">
        <span className="cell-strong">{row.channel_code}</span>
        <span className="cell-muted" style={{ fontSize: 11 }}>{row.label}</span>
      </div>
    ) },
    { key: 'mode', header: '模式', render: (row) => <ModeBadge mode={row.mode} /> },
    /*
     * 分组列。它回答的是「这条通道对哪些员工可见」—— 一个光看通道名完全看不出来、
     * 但决定了谁能看到这条通道的事实。没有这一列，新建通道归错组要等到
     * 「某某说他看不见那条通道」才会被发现。
     */
    { key: 'group', header: '分组', render: (row) => (
      <span className="cell-muted">{row.channel_group_name}</span>
    ) },
    { key: 'status', header: '状态', render: (row) => (
      <div className="row tight">
        <StatusBadge status={row.status} />
        {row.health_score < 100 && (
          <Badge tone={row.health_score < 50 ? 'bad' : 'warn'}>健康 {row.health_score}</Badge>
        )}
      </div>
    ) },
    { key: 'scope', header: '受理范围', render: (row) => (
      <div className="stack tight" style={{ fontSize: 11.5 }}>
        <span className="mono">{row.currencies.join(' ') || '不限币种'}</span>
        <span className="cell-muted mono">{row.card_brands.join(' ') || '不限卡组'}</span>
      </div>
    ) },
    // 限额没有绑定币种（一个通道可能同时受理 USD EUR JPY），
    // 所以只有该通道恰好只受理一种币时才格式化 —— 见 MinorAmount
    { key: 'caps', header: '限额（单/日）', numeric: true, render: (row) => (
      <div className="stack tight">
        <span className="num">
          {row.cap_single === null ? '不限' : <MinorAmount value={row.cap_single} currencies={row.currencies} />}
        </span>
        <span className="num cell-muted">
          {row.cap_daily === null ? '不限' : <MinorAmount value={row.cap_daily} currencies={row.currencies} />}
        </span>
      </div>
    ) },
    { key: 'routing', header: '优先级 / 权重', numeric: true, render: (row) => (
      <span className="num">{row.priority} / {row.weight}</span>
    ) },
    // 成本只有平台拿得到，代理侧这一列的数据根本不下发
    ...(session.isPlatform ? [{
      key: 'cost',
      header: '成本',
      numeric: true,
      render: (row: Channel) => (
        <span className="num">
          {row.cost_rate_bps === undefined ? '—' : formatBps(row.cost_rate_bps)}
        </span>
      ),
    }] : []),
    { key: 'settlement', header: '清分模型', render: (row) => (
      <Badge tone={row.settlement_model === 'platform_managed' ? 'info' : 'neutral'}>
        {row.settlement_model === 'platform_managed' ? '平台归集' : '通道直清'}
      </Badge>
    ) },
    /*
     * 付款页跳转。只有后端下发了 payment_links 才有这一列 —— 没下发说明你无权看
     * 收款链接页面（例如 ops），这时连列头都不出现，免得看到一列永远为空的「付款页」
     * 还以为是数据坏了。
     */
    ...(showLinks
      ? [{ key: 'pay', header: '付款页', render: (row: Channel) => <PayLinks links={row.payment_links ?? []} /> }]
      : []),
    { key: 'actions', header: '', render: (row) => (
      <Button size="sm" onClick={() => { setEditing(row); }}>
        {canManage ? '配置' : '详情'}
      </Button>
    ) },
  ];

  return (
    <>
      <div className="row">
        <h1>通道账户</h1>
        <ScopeBanner context="这些通道。这个账号是只读的，改配置、发授权都会被拒绝。" />
        <span style={{ flex: 1 }} />
        {canManage && (
          <Button variant="primary" onClick={() => { setCreating(true); }}>接入通道</Button>
        )}
      </div>

      {!session.isPlatform && (
        <div className="alert" data-tone="info">
          <span className="alert-title">这里只列出你有权使用的通道</span>
          <span>通道账户由平台持有并配置，你看到的是非敏感的受理范围与限额，用来决定给商户下发哪一个。</span>
        </div>
      )}

      <div className="filters">
        <Field label="模式">
          <Select
            value={mode}
            onChange={setMode}
            placeholder="全部"
            options={[{ value: 'test', label: 'test' }, { value: 'live', label: 'live' }]}
          />
        </Field>
        <Field label="状态">
          <Select
            value={status}
            onChange={setStatus}
            placeholder="全部"
            options={[
              { value: 'active', label: '启用' },
              { value: 'paused', label: '暂停' },
              { value: 'disabled', label: '停用' },
            ]}
          />
        </Field>
        <Field label="分组">
          <Select
            value={groupFilter}
            onChange={setGroupFilter}
            placeholder="全部"
            options={(groups.data?.data ?? []).map((g) => ({
              value: g.id,
              label: `${g.name}（${String(g.channel_count)} 条）`,
            }))}
          />
        </Field>
      </div>

      <Card flush>
        <DataTable
          columns={columns}
          rows={list.data?.data ?? []}
          loading={list.loading}
          error={list.error}
          rowKey={(row) => row.id}
          empty={session.isPlatform ? '还没有接入任何通道' : '你还没有被授权任何通道'}
          emptyHint={
            session.isPlatform
              ? '接入通道需要该通道的完整凭证。缺任何一个键都会被拒绝 —— 不写半截通道。'
              : '通道授权由平台下发。拿到授权后你才能给自己的商户下发 MID。'
          }
        />
      </Card>

      {creating && (
        <CreateChannelModal
          onClose={() => { setCreating(false); }}
          onDone={() => { setCreating(false); list.reload(); }}
        />
      )}
      {editing !== null && (
        <ChannelModal
          channel={editing}
          canManage={canManage}
          onClose={() => { setEditing(null); }}
          onDone={() => { setEditing(null); list.reload(); }}
        />
      )}
    </>
  );
}

function CreateChannelModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [channelCode, setChannelCode] = useState('authorizedotnet');
  const [label, setLabel] = useState('');
  const [mode, setMode] = useState<'test' | 'live'>('test');
  const [currencies, setCurrencies] = useState('USD');
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [groupId, setGroupId] = useState('');

  /*
   * 分组清单。空 deps 是刻意的：它与表单状态无关，跟着 groupId 变会打成
   * 「取数 → 重渲染 → 再取数」的循环（712d92e 修过两处同样的东西）。
   */
  const groups = useAsync<{ data: readonly ChannelGroup[] }>('/v1/channel-groups', {}, []);
  const groupOptions = groups.data?.data ?? [];

  const reqs = useCredentialRequirements();
  const required = reqs.map[channelCode] ?? [];
  const missing = required.filter((key) => (credentials[key] ?? '').trim() === '');
  /*
   * 键名还没拿到之前不许提交。这时一个输入框都渲染不出来，提交必然被后端以
   * 「缺少凭证字段」打回 —— 而人在界面上找不到那个字段在哪，只会一脸茫然。
   */
  const requirementsReady = !reqs.loading && reqs.error === null && required.length > 0;

  const create = useMutation(async () => await request<Channel>('/v1/channels', {
    method: 'POST',
    body: {
      channel_code: channelCode,
      label,
      mode,
      credentials,
      currencies: parseList(currencies, 'upper'),
      channel_group_id: groupId,
    },
  }));

  return (
    <Modal
      title="接入通道"
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>取消</Button>
          <Button
            variant="primary"
            disabled={create.pending || label.trim() === '' || groupId === '' || missing.length > 0 || !requirementsReady}
            onClick={() => { void create.run().then((result) => { if (result !== null) onDone(); }); }}
          >
            {create.pending ? '接入中…' : '接入'}
          </Button>
        </>
      }
    >
      <ErrorBox error={create.error} />

      <div className="grid cols-2">
        <Field label="通道">
          <Select
            value={channelCode}
            onChange={(value) => { setChannelCode(value); setCredentials({}); }}
            options={reqs.codes.map((value) => ({ value, label: value }))}
          />
        </Field>
        <Field label="模式" hint="live 是真钱，凭证也必须是生产凭证。">
          <Select
            value={mode}
            onChange={(value) => { setMode(value as 'test' | 'live'); }}
            options={[{ value: 'test', label: 'test' }, { value: 'live', label: 'live' }]}
          />
        </Field>
      </div>

      {mode === 'live' && (
        <div className="alert" data-tone="bad">
          <span className="alert-title">这是 live 通道</span>
          <span>接入后经由它的每一笔交易都会真实扣款。请确认凭证来自生产账户。</span>
        </div>
      )}

      <Field label="备注名" hint="用来在列表里区分同一通道的多个账户，例如「主账户」「备用-高风险」。">
        <TextInput value={label} onChange={setLabel} placeholder="主账户" />
      </Field>

      {/*
        * 分组必选，没有默认值。
        *
        * 它决定这条通道对哪些员工可见：配了这个组的人立刻看得到它，没配的人看不到。
        * 给一个默认值就等于替人做了可见性决定，而那个决定在某天一定会咬人 ——
        * 要么新通道悄悄进了所有人的视野，要么它对所有受限成员静默消失。
        */}
      <Field
        label="分组"
        hint="决定这条通道对哪些员工可见。配了这个分组的成员会立刻看到它，没配的看不到。"
      >
        <Select
          value={groupId}
          onChange={setGroupId}
          placeholder={groups.loading ? '加载中…' : '请选择分组'}
          options={groupOptions.map((g) => ({
            value: g.id,
            label: g.channel_count === 0 ? `${g.name}（空组）` : `${g.name}（${String(g.channel_count)} 条）`,
          }))}
        />
      </Field>

      <Field label="受理币种" hint="空格或逗号分隔。留空表示不限。">
        <TextInput value={currencies} onChange={setCurrencies} placeholder="USD EUR" />
      </Field>

      <div className="stack tight">
        <span className="field-label">凭证</span>
        <span className="field-hint">
          全部必填 —— 缺任何一个键都会被拒绝，系统不写「以后再补」的半截通道。
          凭证以 AES-256-GCM 加密落库，且密文绑定该行 id，复制到别的通道行上会解密失败。
        </span>
        {required.map((key) => (
          <Field key={key} label={key}>
            <TextInput
              value={credentials[key] ?? ''}
              onChange={(value) => { setCredentials((prev) => ({ ...prev, [key]: value })); }}
              type="password"
            />
          </Field>
        ))}
      </div>
    </Modal>
  );
}

/**
 * 空格/逗号分隔的列表 ⇄ 数组。留空表示「不限」。
 *
 * **大小写必须按字段区分。** 币种与国家在库里是大写（`USD` / `US`），
 * 卡组织是小写（`visa`，后端 zod 就是小写 enum）。
 * 统一转大写会有两个后果，而且都不报错：
 *   1. 脏值判定恒为真 —— `VISA` 永远不等于 `visa`，页面上永远显示「有未保存的改动」
 *   2. 保存时把 `VISA` 发出去，被后端 enum 拒绝
 * 所以这里让调用方显式指定要哪一种。
 */
function parseList(input: string, casing: 'upper' | 'lower'): string[] {
  return input
    .split(/[\s,]+/)
    .filter((item) => item !== '')
    .map((item) => (casing === 'upper' ? item.toUpperCase() : item.toLowerCase()));
}

/**
 * 上调成本被拒时的专用展示。
 *
 * 后端的 `assertCostRaiseSafe` 会在 details.conflicts 里列出所有「报价低于新成本」的
 * 下游费率规则。这个信息比错误文案本身有用得多 —— 它直接告诉你要先去改哪几条价，
 * 所以不能让它淹没在一坨 JSON 里。
 */
function CostConflicts({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) return null;
  const details = error.details as { conflicts?: readonly Record<string, string>[] } | null;
  const conflicts = details?.conflicts;
  if (conflicts === undefined || conflicts.length === 0) return null;

  return (
    <div className="stack tight">
      <div className="alert" data-tone="bad">
        <span className="alert-title">这些下游费率会低于新成本，必须先改价</span>
        <span>
          计费要求各层报价从内到外单调不减，最内层就是通道成本。成本一旦高过下游报价，
          该通道上的交易会在<strong>创建阶段</strong>就全线失败，而且控制台上毫无征兆。
        </span>
      </div>
      <div className="table-scroll">
        <table className="data">
          <thead>
            <tr>
              <th>组织</th><th>费率规则</th><th className="num">当前费率</th><th className="num">当前固定费</th>
            </tr>
          </thead>
          <tbody>
            {conflicts.map((row) => (
              <tr key={String(row['fee_rule_id'])}>
                <td>
                  <div className="stack tight">
                    <span className="cell-strong">{String(row['org_name'])}</span>
                    <CopyId id={String(row['org_id'])} />
                  </div>
                </td>
                <td><CopyId id={String(row['fee_rule_id'])} /></td>
                <td className="num">{formatBps(Number(row['rate_bps']))}</td>
                <td className="num">{String(row['fixed_minor'])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

type ConfigTab = 'basic' | 'scope' | 'caps' | 'cost' | 'credentials';

const CONFIG_TABS: readonly { readonly key: ConfigTab; readonly label: string }[] = [
  { key: 'basic', label: '基础' },
  { key: 'scope', label: '受理范围' },
  { key: 'caps', label: '限额' },
  { key: 'cost', label: '平台成本' },
  { key: 'credentials', label: '凭证' },
];

function ChannelModal({
  channel, canManage, onClose, onDone,
}: {
  channel: Channel;
  canManage: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const detail = useAsync<Channel>(`/v1/channels/${channel.id}`);

  const [label, setLabel] = useState(channel.label);
  const [status, setStatus] = useState(channel.status);
  const [priority, setPriority] = useState(String(channel.priority));
  const [weight, setWeight] = useState(String(channel.weight));
  const [delayDays, setDelayDays] = useState(String(channel.settlement_delay_days));

  const [currencies, setCurrencies] = useState(channel.currencies.join(' '));
  const [countries, setCountries] = useState(channel.countries.join(' '));
  const [brands, setBrands] = useState(channel.card_brands.join(' '));

  const [capSingle, setCapSingle] = useState(channel.cap_single ?? '');
  const [capDaily, setCapDaily] = useState(channel.cap_daily ?? '');
  const [capMonthly, setCapMonthly] = useState(channel.cap_monthly ?? '');

  // 成本只有平台拿得到（代理侧后端根本不下发这两个字段）
  const costEditable = channel.cost_rate_bps !== undefined;
  const [costRate, setCostRate] = useState(String(channel.cost_rate_bps ?? 0));
  const [costFixed, setCostFixed] = useState(String(channel.cost_fixed_minor ?? '0'));

  const [rotating, setRotating] = useState(false);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [tab, setTab] = useState<ConfigTab>('basic');

  const required = useCredentialRequirements().map[channel.channel_code] ?? [];
  const missing = required.filter((key) => (credentials[key] ?? '').trim() === '');

  const listChanged = (
    next: string,
    before: readonly string[],
    casing: 'upper' | 'lower',
  ): boolean => parseList(next, casing).join(' ') !== [...before].join(' ');

  const capChanged = (next: string, before: string | null): boolean =>
    (next.trim() === '' ? null : next.trim()) !== before;

  const costRaised =
    costEditable &&
    (Number(costRate) > (channel.cost_rate_bps ?? 0) ||
      BigInt(costFixed || '0') > BigInt(channel.cost_fixed_minor ?? '0'));

  /*
   * 删除只对「建错了、从没用过」的通道有意义。后端会逐张表查引用，有任何一条就拒绝
   * 并让人改用「停用」—— 所以这里不做前端预判：预判会和后端的判据漂移，
   * 而漂移的方向是「按钮亮着但点了报错」或者更糟的「按钮灰着但其实能删」。
   */
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const remove = useMutation(async () =>
    await request(`/v1/channels/${channel.id}`, { method: 'DELETE' }));

  const save = useMutation(async () => await request<Channel>(`/v1/channels/${channel.id}`, {
    method: 'PATCH',
    body: {
      ...(label !== channel.label ? { label } : {}),
      ...(status !== channel.status ? { status } : {}),
      ...(Number(priority) !== channel.priority ? { priority: Number(priority) } : {}),
      ...(Number(weight) !== channel.weight ? { weight: Number(weight) } : {}),
      ...(Number(delayDays) !== channel.settlement_delay_days
        ? { settlement_delay_days: Number(delayDays) } : {}),

      ...(listChanged(currencies, channel.currencies, 'upper')
        ? { currencies: parseList(currencies, 'upper') } : {}),
      ...(listChanged(countries, channel.countries, 'upper')
        ? { countries: parseList(countries, 'upper') } : {}),
      ...(listChanged(brands, channel.card_brands, 'lower')
        ? { card_brands: parseList(brands, 'lower') } : {}),

      // 限额显式发 null 才能清空 —— undefined 会被后端当成「不改」
      ...(capChanged(capSingle, channel.cap_single)
        ? { cap_single: capSingle.trim() === '' ? null : capSingle.trim() } : {}),
      ...(capChanged(capDaily, channel.cap_daily)
        ? { cap_daily: capDaily.trim() === '' ? null : capDaily.trim() } : {}),
      ...(capChanged(capMonthly, channel.cap_monthly)
        ? { cap_monthly: capMonthly.trim() === '' ? null : capMonthly.trim() } : {}),

      ...(costEditable && Number(costRate) !== channel.cost_rate_bps
        ? { cost_rate_bps: Number(costRate) } : {}),
      ...(costEditable && costFixed !== String(channel.cost_fixed_minor ?? '0')
        ? { cost_fixed_minor: costFixed.trim() === '' ? '0' : costFixed.trim() } : {}),

      ...(rotating ? { credentials } : {}),
    },
  }));

  // 按页签分组算 dirty：切走之后仍然要能看见「那一组我改过」
  const basicDirty =
    label !== channel.label ||
    status !== channel.status ||
    Number(priority) !== channel.priority ||
    Number(weight) !== channel.weight ||
    Number(delayDays) !== channel.settlement_delay_days;
  const scopeDirty =
    listChanged(currencies, channel.currencies, 'upper') ||
    listChanged(countries, channel.countries, 'upper') ||
    listChanged(brands, channel.card_brands, 'lower');
  const capsDirty =
    capChanged(capSingle, channel.cap_single) ||
    capChanged(capDaily, channel.cap_daily) ||
    capChanged(capMonthly, channel.cap_monthly);
  const costDirty =
    costEditable &&
    (Number(costRate) !== channel.cost_rate_bps ||
      costFixed !== String(channel.cost_fixed_minor ?? '0'));

  const dirty =
    basicDirty || scopeDirty || capsDirty || costDirty || (rotating && missing.length === 0);

  return (
    <Modal
      title={`${channel.channel_code} · ${channel.label}`}
      onClose={onClose}
      wide
      {...(canManage
        ? {
            footer: (
              <>
                {confirmingDelete ? (
                  <>
                    <span className="cell-muted">删除后不可恢复，确定？</span>
                    <Button onClick={() => { setConfirmingDelete(false); }}>再想想</Button>
                    <Button
                      variant="danger"
                      disabled={remove.pending}
                      onClick={() => { void remove.run().then((r) => { if (r !== null) onDone(); }); }}
                    >
                      {remove.pending ? '删除中…' : '确认删除'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="danger" onClick={() => { setConfirmingDelete(true); }}>删除</Button>
                    <span style={{ flex: 1 }} />
                    <Button onClick={onClose}>取消</Button>
                    <Button
                      variant="primary"
                      disabled={save.pending || !dirty || (rotating && missing.length > 0)}
                      onClick={() => { void save.run().then((result) => { if (result !== null) onDone(); }); }}
                    >
                      {save.pending ? '保存中…' : '保存'}
                    </Button>
                  </>
                )}
              </>
            ),
          }
        : {})}
    >
      <ErrorBox error={save.error} />
      <ErrorBox error={remove.error} />
      <CostConflicts error={save.error} />
      <ErrorBox error={detail.error} />

      <DetailGrid rows={[
        ['通道账户 id', <CopyId key="id" id={channel.id} full />],
        ['模式', <ModeBadge key="m" mode={channel.mode} />],
        ['清分模型', <span key="sm">
          {channel.settlement_model === 'platform_managed' ? '平台归集清分' : '通道直清'}
        </span>],
        ['健康分', <span key="h" className="mono">{channel.health_score}</span>],
      ]} />

      {!canManage && (
        <DetailGrid rows={[
          ['结算周期', <span key="d" className="mono">T+{channel.settlement_delay_days}</span>],
          ['受理币种', <span key="c" className="mono">{channel.currencies.join(' ') || '不限'}</span>],
          ['受理国家', <span key="co" className="mono">{channel.countries.join(' ') || '不限'}</span>],
          ['卡组织', <span key="b" className="mono">{channel.card_brands.join(' ') || '不限'}</span>],
          ['单笔限额', <MinorAmount key="cs" value={channel.cap_single} currencies={channel.currencies} />],
          ['日限额', <MinorAmount key="cd" value={channel.cap_daily} currencies={channel.currencies} />],
        ]} />
      )}

      <RecentWebhooks channelId={channel.id} />

      <div className="alert" data-tone="warn">
        <span className="alert-title">清分模型不可改</span>
        <span>
          退款记账按<strong>退款那一刻</strong>读到的模型分叉。这个通道上只要还有可能发生退款的存量交易，
          后端就会拒绝修改 —— 改了会让已入账的货款永远扣不回来。要换模型请新建一个通道账户并迁移绑定。
        </span>
      </div>

      {canManage && (
        <>
          <Tabs
            tabs={CONFIG_TABS}
            active={tab}
            onChange={setTab}
            dirty={{
              basic: basicDirty,
              scope: scopeDirty,
              caps: capsDirty,
              cost: costDirty,
              credentials: rotating && missing.length === 0,
            }}
          />

          {tab === 'basic' && (
            <div className="tab-panel">
              <Field label="备注名" hint="用来区分同一通道下的多个账户">
                <TextInput value={label} onChange={setLabel} />
              </Field>
              <div className="grid cols-2">
                <Field label="状态" hint="暂停后不再参与路由，存量交易不受影响。">
                  <Select
                    value={status}
                    onChange={(value) => { setStatus(value as Channel['status']); }}
                    options={[
                      { value: 'active', label: '启用' },
                      { value: 'paused', label: '暂停' },
                      { value: 'disabled', label: '停用' },
                    ]}
                  />
                </Field>
                <Field label="结算周期（天）" hint="T+N。影响待结算转可用的时点。">
                  <TextInput value={delayDays} onChange={setDelayDays} numeric />
                </Field>
                <Field label="优先级" hint="越小越先被选中">
                  <TextInput value={priority} onChange={setPriority} numeric />
                </Field>
                <Field label="权重" hint="同优先级内按权重分流">
                  <TextInput value={weight} onChange={setWeight} numeric />
                </Field>
              </div>
            </div>
          )}

          {tab === 'scope' && (
            <div className="tab-panel">
              <span className="field-hint">
                空格或逗号分隔，<strong>留空表示不限</strong>。路由时不匹配的交易不会走这个通道。
              </span>
              <Field label="受理币种">
                <TextInput value={currencies} onChange={setCurrencies} placeholder="USD EUR" />
              </Field>
              <Field label="受理国家">
                <TextInput value={countries} onChange={setCountries} placeholder="US CA" />
              </Field>
              <Field label="卡组织">
                <TextInput value={brands} onChange={setBrands} placeholder="visa mastercard amex" />
              </Field>
            </div>
          )}

          {tab === 'caps' && (
            <div className="tab-panel">
              <span className="field-hint">
                单位是<strong>最小单位整数</strong>（USD 填分：500000 = $5,000.00）。留空表示不限。
                限额不绑定币种，多币种通道请按最严的那一种设置。
              </span>
              <div className="grid cols-3">
                <Field label="单笔">
                  <TextInput value={capSingle} onChange={setCapSingle} placeholder="不限" numeric />
                </Field>
                <Field label="每日">
                  <TextInput value={capDaily} onChange={setCapDaily} placeholder="不限" numeric />
                </Field>
                <Field label="每月">
                  <TextInput value={capMonthly} onChange={setCapMonthly} placeholder="不限" numeric />
                </Field>
              </div>
            </div>
          )}

          {tab === 'cost' && (
            <div className="tab-panel">
              {!costEditable ? (
                <div className="alert" data-tone="info">
                  <span className="alert-title">成本只对平台可见</span>
                  <span>代理知道平台的进价就没法谈价了，所以后端根本不下发这两个字段。</span>
                </div>
              ) : (
                <>
                  <span className="field-hint">
                    这是<strong>你付给通道方的进价</strong>，是整条定价链的最内层。
                    所有下游报价必须不低于它，否则那一层就是在倒贴。代理与商户永远看不到这两个数。
                  </span>
                  <div className="grid cols-2">
                    <Field
                      label="费率（基点）"
                      hint={`当前输入 = ${formatBps(Number(costRate) || 0)}。100 基点 = 1%，250 = 2.50%。`}
                    >
                      <TextInput value={costRate} onChange={setCostRate} numeric />
                    </Field>
                    <Field
                      label="固定费（最小单位）"
                      hint={
                        channel.currencies.length === 1
                          ? `按 ${channel.currencies[0]} 计：30 = 0.30`
                          : '每笔另收的固定金额。30 = USD 场景下的 $0.30。'
                      }
                    >
                      <TextInput value={costFixed} onChange={setCostFixed} numeric />
                    </Field>
                  </div>

                  {costRaised && (
                    <div className="alert" data-tone="warn">
                      <span className="alert-title">你在上调成本 —— 保存前后端会先预演一遍</span>
                      <span>
                        只要还有任何一条下游费率规则的报价低于新成本，保存就会被拒绝并列出是哪几条。
                        这是刻意的：放行的话，该通道上的交易会在创建阶段全线失败，而且没有任何征兆。
                        正确顺序是<strong>先调高下游价，再调高成本</strong>。
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab === 'credentials' && (
            <div className="tab-panel">
              {/*
                凭证键名随通道而变：Authorize.Net 是 apiLoginId / transactionKey / signatureKey，
                Stripe 是 secretKey / webhookSecret，Square 是 accessToken / locationId /
                webhookSignatureKey。这张表与后端 REQUIRED_CREDENTIAL_KEYS 逐字对应。
              */}
              <div className="alert" data-tone="info">
                <span className="alert-title">{channel.channel_code} 需要这几项</span>
                <span>
                  {required.map((key) => (
                    <span key={key} className="mono" style={{ marginRight: 10 }}>{key}</span>
                  ))}
                </span>
              </div>

              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={rotating}
                  onChange={(e) => { setRotating(e.target.checked); setCredentials({}); }}
                />
                我要更换凭证
              </label>

              {!rotating ? (
                <div className="stack tight">
                  {required.map((key) => (
                    <CredentialRow
                      key={key}
                      channelId={channel.id}
                      credKey={key}
                      preview={(detail.data?.credential_preview ?? []).find((p) => p.key === key)}
                    />
                  ))}
                  <span className="field-hint">
                    这里显示的是前 4 后 4，中间是遮掉的。点眼睛看完整值 ——
                    <strong>每次揭示都会写审计日志</strong>（记谁在什么时候读了哪一项，不记值本身）。
                  </span>
                </div>
              ) : (
                <>
                  <div className="alert" data-tone="warn">
                    <span className="alert-title">整组替换，必须全部填写</span>
                    <span>
                      只想改其中一项，也要把其余的一起重新填一遍 —— 后端按整组校验齐全性，
                      缺任何一个键都会被拒绝，不写半截通道。
                    </span>
                  </div>
                  {required.map((key) => (
                    <Field key={key} label={key}>
                      <TextInput
                        value={credentials[key] ?? ''}
                        onChange={(value) => { setCredentials((prev) => ({ ...prev, [key]: value })); }}
                        type="password"
                      />
                    </Field>
                  ))}
                  {missing.length > 0 && (
                    <span className="field-hint">
                      还差：<span className="mono">{missing.join(' ')}</span>
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}


interface WebhookEventRow {
  readonly id: string;
  readonly event_type: string;
  readonly external_id: string;
  readonly signature_valid: boolean;
  readonly processed_at: string | null;
  readonly process_error: string | null;
  readonly received_at: string;
  readonly has_object: boolean;
}

/**
 * 最近回调。
 *
 * ## 为什么这块面板值得存在
 *
 * 配 webhook 是整条接入链路里**唯一一个「配错了完全没有症状」**的环节：
 *
 *   - 密钥填错   → 每条回调 401，但通道后台只显示「已送达」
 *   - payload 选成 Thin → 验签通过、事件落库，却什么都提取不到
 *
 * 两种都不报错，症状统一是「结算状态永远不更新」，而那要等到对账才发现。
 * 有了这块面板，在通道后台点一次 test webhook，当场就能看见对没对。
 *
 * ## 三种状态要分开说
 *
 * 只看「验签通过」不够 —— Thin payload 的验签**照样通过**（签名对字节算，
 * 与内容无关）。所以这里把「验签」与「处理」拆成两列，并在
 * 「验签过了但没处理且报文里没有 data.object」时直接点名 Thin。
 * 那是最难自己想到的一种错。
 */
function RecentWebhooks({ channelId }: { channelId: string }) {
  const list = useAsync<{ data: readonly WebhookEventRow[] }>(
    `/v1/channels/${channelId}/webhooks`, {}, [channelId],
  );
  const rows = list.data?.data ?? [];

  if (list.loading) return <span className="cell-muted">最近回调加载中…</span>;

  return (
    <div className="stack tight">
      <span className="field-label">最近回调</span>
      <ErrorBox error={list.error} />

      {rows.length === 0 ? (
        <div className="alert" data-tone="info">
          <span className="alert-title">还没有收到过任何回调</span>
          <span>
            如果通道那边已经配好了 webhook 端点，去点一次「发送测试事件」，
            这里就会出现一条。一直是空的说明端点地址没配、或者配到了别的通道上。
            地址应当是 <span className="mono">/v1/webhooks/{'{'}通道类型{'}'}/{channelId}</span>。
          </span>
        </div>
      ) : (
        <div className="stack tight">
          {rows.map((r) => {
            // 验签过、没处理、报文里又没有 data.object —— 这基本只有一个原因
            const looksThin = r.signature_valid && r.processed_at === null && !r.has_object;
            return (
              <div key={r.id} className="row tight" style={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span className="cell-muted mono" style={{ fontSize: 12, minWidth: 130 }}>
                  {formatDateTime(r.received_at)}
                </span>
                <span className="mono" style={{ fontSize: 12, minWidth: 220 }}>{r.event_type}</span>
                <Badge tone={r.signature_valid ? 'good' : 'bad'}>
                  {r.signature_valid ? '验签通过' : '验签失败'}
                </Badge>
                {r.signature_valid && (
                  <Badge tone={r.processed_at !== null ? 'good' : 'warn'}>
                    {r.processed_at !== null ? '已处理' : '未处理'}
                  </Badge>
                )}
                {looksThin && (
                  <span className="cell-muted" style={{ fontSize: 12 }}>
                    报文里没有 data.object —— 多半是 payload 选成了 Thin，改成 Snapshot
                  </span>
                )}
                {r.process_error !== null && r.process_error !== '' && (
                  <span className="cell-muted" style={{ fontSize: 12 }}>{r.process_error}</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {rows.length > 0 && rows.every((r) => !r.signature_valid) && (
        <div className="alert" data-tone="bad">
          <span className="alert-title">最近的回调全部验签失败</span>
          <span>
            通道发来的签名与这里配的密钥对不上。两种可能：密钥填错了，
            或者端点地址指向了<strong>另一条通道</strong>的 id —— 那样会拿错密钥去验。
            回通道后台复制一次签名密钥重填，并核对端点 URL 末尾的 id 是
            <span className="mono"> {channelId}</span>。
          </span>
        </div>
      )}
    </div>
  );
}

interface CredentialPreview {
  readonly key: string;
  readonly configured: boolean;
  readonly masked: string | null;
}

/**
 * 一项凭证：配了就显示掩码 + 眼睛，没配就明说没配。
 *
 * ## 为什么掩码这一层是有价值的
 *
 * 在它之前，这里只有一句「当前值不会显示」。于是「我到底配的是不是那把 key」
 * 没有任何办法回答，而这两周踩的两次错都属于这一类：一次填成了 27 位的
 * `mk_…`（根本不是 Stripe 凭证），一次填了个 8 位的假 `whsec_12`。
 * 有前 4 后 4，两次都能一眼看出来 —— 而前后 4 位不足以使用任何密钥。
 *
 * ## 眼睛是另一回事
 *
 * 点它会取回完整值，那是真的把密钥交出去。所以它走一个单独的、**会写审计**
 * 的接口，而不是顺手塞进详情响应里。取回的值只存在于这个组件的 state，
 * 不写 localStorage、不进 URL、离开弹窗即消失。
 */
function CredentialRow({
  channelId, credKey, preview,
}: {
  channelId: string;
  credKey: string;
  preview: CredentialPreview | undefined;
}) {
  const [shown, setShown] = useState<string | null>(null);
  const reveal = useMutation(async () =>
    await request<{ key: string; value: string }>(
      `/v1/channels/${channelId}/credentials/reveal`,
      { method: 'POST', body: { key: credKey } },
    ));

  const configured = preview?.configured === true;

  return (
    <Field label={credKey}>
      {!configured ? (
        <span className="cell-muted">未配置 —— 勾上「我要更换凭证」填入</span>
      ) : (
        <div className="row tight" style={{ alignItems: 'center' }}>
          <span className="mono" style={{ flex: 1, wordBreak: 'break-all' }}>
            {shown ?? preview?.masked}
          </span>
          <Button
            size="sm"
            disabled={reveal.pending}
            onClick={() => {
              if (shown !== null) { setShown(null); return; }
              void reveal.run().then((r) => { if (r !== null) setShown(r.value); });
            }}
          >
            {reveal.pending ? '…' : shown !== null ? '隐藏' : '显示'}
          </Button>
        </div>
      )}
      <ErrorBox error={reveal.error} />
    </Field>
  );
}
